import type LayerViewModel from "@esWebAPI/es/map/LayerViewModel";
import type { Criteria, NearestCriteria } from "@esWebAPI/es/model/Criteria";
import type { Attribute } from "@esWebAPI/es/model/Feature";

import AbstractSpatialFilterControl from "./AbstractSpatialFilterControl";

const TYPE = "Nearest";
const PREFIX = "ES_NEAR_DIST_";
const NEAREST_MAX = 50;
const MAX_KMS = es.spatialLimits.maxDistanceKilometer;
const MAX_FEET_US = es.spatialLimits.maxDistanceFoot;
const MAX_FEET = es.spatialLimits.maxDistanceFoot;
const MAX_METERS = es.spatialLimits.maxDistanceMeter;
const MAX_MILES = es.spatialLimits.maxDistanceMile;

export default class NearestFilterControl extends AbstractSpatialFilterControl {
    private name: string;
    private geometry?: any[];
    private geoShapeKey?: string;

    private unit: string;
    private numNeighbours: number;
    private distance: number;

    constructor(layerViewModel: LayerViewModel, geometry?: any[], geoShapeKey?: string) {
        super(layerViewModel, TYPE, $i18n('Search_Nearest'));

        this.name = this.generateUniqName(PREFIX);
        this.geometry = geometry;
        this.geoShapeKey = geoShapeKey;

        // Set the default values
        this.unit = es.util.getDefaultDistanceUnit();
        this.numNeighbours = 0;
        this.distance = 100;
    };

    // TODO should have a common distance validation
    private validateDistance(unit: string, distance: number): string | null {
        switch (unit) {
            case "SURVEY_FOOT":
                return distance > MAX_FEET_US ? ($i18n('_SpatialFilter_Nearest_Error_Msg_Above_Maximum', [MAX_FEET_US + " " + $i18n('_Feet_US')])) : null;

            case "FOOT":
                return distance > MAX_FEET ? ($i18n('_SpatialFilter_Nearest_Error_Msg_Above_Maximum', [MAX_FEET + " " + $i18n('Feet')])) : null;

            case "METER":
                return distance > MAX_METERS ? ($i18n('_SpatialFilter_Nearest_Error_Msg_Above_Maximum', [MAX_METERS + " " + $i18n('Meters')])) : null;

            case "KILOMETER":
                return distance > MAX_KMS ? ($i18n('_SpatialFilter_Nearest_Error_Msg_Above_Maximum', [MAX_KMS + " " + $i18n('Kms')])) : null;

            case "MILE":
                return distance > MAX_MILES ? ($i18n('_SpatialFilter_Nearest_Error_Msg_Above_Maximum', [MAX_MILES + " " + $i18n('Miles')])) : null;
            default:
                es.log.error("Unknown distance");
                return null;
        }
    }

    getLayerTitle(): string {
        return this.layerViewModel.getTitle();
    }

    toCriteria(): NearestCriteria | null {
        if (!this.unit || !this.isValid()) {
            return null;
        }

        const criteria: NearestCriteria = {
            $type: this.getType(),
            name: this.name,
            numNeighbours: this.numNeighbours,
            distance: this.distance,
            unit: this.unit
        };

        this.setGeometry(criteria, this.geoShapeKey, this.geometry);
        return criteria;
    };

    getDynamicAttributes(): JQueryDeferred<Attribute[]> {
        if (this.isValid()) {
            const dynamicAttribute: Attribute = {
                $type: 'CalculateDistanceAttribute',
                name: this.name,
                criteriaName: this.name,
                title: 'Distance (' + $i18n('_Distance_Unit_' + this.unit) + ')',
                type: 'DECIMAL',
                unit: this.unit
            };

            this.setGeometry(dynamicAttribute, this.geoShapeKey, this.geometry);
            return es.util.deferred.resolved([dynamicAttribute]);
        }

        return es.util.deferred.resolved([]);
    }

    fromCriteria(criteria: Criteria): undefined {
        if (criteria.$type !== this.getType()) {
            throw new Error("Trying to update filter with invalid criteria");
        }

        this.distance = criteria.distance;
        this.unit = criteria.unit;
        this.name = criteria.name;
        this.numNeighbours = criteria.numNeighbours;

        
        if (criteria.geoShapeKey) {
            this.geoShapeKey = criteria.geoShapeKey;
        } else {
            this.geometry = criteria.geometry;
        }
    }

    private checkInputValue(unit: string, distance: number, numNeighbours: number): string | null {
        if (isNaN(distance) || distance <= 0) {
            return $i18n('_Filter_Error_Msg_Not_Supported');
        }

        if (isNaN(numNeighbours) || numNeighbours <= 0) {
            return $i18n('_Filter_Error_Msg_Not_Supported');
        }

        if (numNeighbours > NEAREST_MAX) {
            return ($i18n('_SpatialFilter_Nearest_Error_Msg_Above_Maximum',[NEAREST_MAX]));
        }

        return this.validateDistance(unit, distance);
    };

    isValid(): boolean {
        return this.checkInputValue(this.unit, this.distance, this.numNeighbours) === null;
    }

    update(unit: string, distance: number | string, numNeighbours: number | string) {
        if (unit != this.unit) {
            es.util.setDefaultDistanceUnit(unit);
        }

        distance = parseFloat(distance);
        numNeighbours = parseFloat(numNeighbours);

        var err = this.checkInputValue(unit, distance, numNeighbours);
        if (err) {
            return err;
        }

        this.unit = unit;
        this.distance = distance;
        this.numNeighbours = numNeighbours;

        this.eventChannel.fireEvent("onUpdate");
    }

    static supportCriteria(criteria: Criteria): boolean {
        return criteria.$type === TYPE;
    }

    static supportAttribute(): boolean {
        return false;
    }

    static fromCriteria(layerViewModel: LayerViewModel, criteria: Criteria): JQueryDeferred<NearestFilterControl> {
        return new NearestFilterControl(layerViewModel).init().done((filter: NearestFilterControl) => {
            filter.fromCriteria(criteria);
        });
    }

    //es.control.NearestFilter = NearestFilter;
}
